/**
 * Свойства абзаца и фрагмента текста (run) в плоском виде.
 *
 * Атрибуты w:spacing и w:ind наследуются по отдельности (стиль может задать
 * интервал «после», а прямое форматирование — только межстрочный), поэтому
 * они разложены на независимые поля.
 */

import { childVal, type XElement } from '../ooxml/xml';
import { parseIntAttr, parseTwips } from '../util/units';

export type Align = 'left' | 'center' | 'right' | 'justify';
export type LineRule = 'auto' | 'exact' | 'atLeast';

export interface ParaProps {
  keepNext?: boolean;
  keepLines?: boolean;
  pageBreakBefore?: boolean;
  widowControl?: boolean;
  numId?: number;
  ilvl?: number;
  /** Интервал перед абзацем, twip. */
  spacingBefore?: number;
  spacingAfter?: number;
  beforeAuto?: boolean;
  afterAuto?: boolean;
  /** Для lineRule=auto — доли строки ×240 (360 = полуторный), иначе twip. */
  line?: number;
  lineRule?: LineRule;
  indLeft?: number;
  indRight?: number;
  /** Отступ первой строки, twip: > 0 — отступ, < 0 — выступ. */
  indFirst?: number;
  /** Отступ первой строки в сотых долях символа (перекрывает indFirst). */
  indFirstChars?: number;
  contextualSpacing?: boolean;
  jc?: Align;
  outlineLvl?: number;
  suppressAutoHyphens?: boolean;
}

export type FontRef = { name: string; theme?: undefined } | { theme: string; name?: undefined };

export interface RunProps {
  fontAscii?: FontRef;
  fontHAnsi?: FontRef;
  fontEastAsia?: FontRef;
  fontCs?: FontRef;
  /** w:hint: при 'eastAsia' кириллицу и греческие буквы Word выводит восточноазиатским шрифтом. */
  fontHint?: 'eastAsia' | 'cs' | 'default';
  b?: boolean;
  i?: boolean;
  caps?: boolean;
  smallCaps?: boolean;
  strike?: boolean;
  dstrike?: boolean;
  vanish?: boolean;
  /** Тип подчёркивания; 'none' — без подчёркивания. */
  u?: string;
  /** 'auto' или RRGGBB. */
  color?: string;
  highlight?: string;
  shading?: string;
  /** Кегль в полупунктах. */
  sz?: number;
  szCs?: number;
  vertAlign?: 'superscript' | 'subscript' | 'baseline';
  lang?: string;
  /** Межзнаковый интервал, twip. */
  spacing?: number;
  /** Масштаб по горизонтали, %. */
  w?: number;
}

/** Переключаемые свойства: в стилях они инвертируют унаследованное значение (ECMA-376, 17.7.3). */
export const TOGGLE_PROPS: ReadonlySet<string> = new Set(['b', 'i', 'caps', 'smallCaps', 'strike', 'dstrike', 'vanish']);

/** Откуда пришло значение свойства — для понятных сообщений и выбора способа исправления. */
export type Origin = 'default' | 'docDefaults' | 'numbering' | 'direct' | `style:${string}` | `table:${string}`;

export interface Resolved<T> {
  props: T;
  origin: Partial<Record<keyof T, Origin>>;
}

export const emptyResolved = <T>(): Resolved<T> => ({ props: {} as T, origin: {} });

export function overlay<T extends object>(target: Resolved<T>, src: T, origin: Origin): void {
  for (const key of Object.keys(src) as Array<keyof T>) {
    const v = src[key];
    if (v === undefined) continue;
    target.props[key] = v;
    target.origin[key] = origin;
  }
}

export function overlayResolved<T extends object>(target: Resolved<T>, src: Resolved<T>): void {
  for (const key of Object.keys(src.props) as Array<keyof T>) {
    const v = src.props[key];
    if (v === undefined) continue;
    target.props[key] = v;
    target.origin[key] = src.origin[key];
  }
}

const onOffString = (v: string | undefined): boolean =>
  !(v === '0' || v === 'false' || v === 'off' || v === 'none');

/** Логическое свойство: <w:b/> — true, <w:b w:val="0"/> — false. */
export function parseOnOff(e: XElement | undefined): boolean | undefined {
  if (!e) return undefined;
  const v = e.attrs['w:val'];
  return v === undefined ? true : onOffString(v);
}

export function normalizeJc(v: string | undefined): Align | undefined {
  switch (v) {
    case 'left':
    case 'start':
      return 'left';
    case 'right':
    case 'end':
      return 'right';
    case 'center':
      return 'center';
    case 'both':
    case 'distribute':
    case 'thaiDistribute':
    case 'lowKashida':
    case 'mediumKashida':
    case 'highKashida':
      return 'justify';
    default:
      return undefined;
  }
}

/** Кегль: полупункты, в Strict возможна запись «14pt». */
function parseHalfPoints(v: string | undefined): number | undefined {
  if (v == null) return undefined;
  if (/pt\s*$/.test(v)) return Math.round(parseFloat(v) * 2);
  return parseIntAttr(v);
}

export function parsePPr(pPr: XElement | undefined): ParaProps {
  const p: ParaProps = {};
  if (!pPr) return p;
  for (const c of pPr.children) {
    if (c.type !== 'element') continue;
    const a = c.attrs;
    switch (c.name) {
      case 'w:keepNext':
        p.keepNext = parseOnOff(c);
        break;
      case 'w:keepLines':
        p.keepLines = parseOnOff(c);
        break;
      case 'w:pageBreakBefore':
        p.pageBreakBefore = parseOnOff(c);
        break;
      case 'w:widowControl':
        p.widowControl = parseOnOff(c);
        break;
      case 'w:numPr': {
        const numId = parseIntAttr(childVal(c, 'w:numId'));
        const ilvl = parseIntAttr(childVal(c, 'w:ilvl'));
        if (numId !== undefined) p.numId = numId;
        if (ilvl !== undefined) p.ilvl = ilvl;
        break;
      }
      case 'w:spacing': {
        const before = parseTwips(a['w:before']);
        const after = parseTwips(a['w:after']);
        if (before !== undefined) p.spacingBefore = before;
        if (after !== undefined) p.spacingAfter = after;
        // beforeLines/afterLines — сотые доли строки (12 пт) и имеют приоритет над before/after.
        const bl = parseIntAttr(a['w:beforeLines']);
        const al = parseIntAttr(a['w:afterLines']);
        if (bl) p.spacingBefore = Math.round(bl * 2.4);
        if (al) p.spacingAfter = Math.round(al * 2.4);
        if (a['w:beforeAutospacing'] !== undefined) p.beforeAuto = onOffString(a['w:beforeAutospacing']);
        if (a['w:afterAutospacing'] !== undefined) p.afterAuto = onOffString(a['w:afterAutospacing']);
        const line = parseIntAttr(a['w:line']);
        if (line !== undefined) {
          p.line = line;
          const rule = a['w:lineRule'];
          p.lineRule = rule === 'exact' || rule === 'atLeast' ? rule : 'auto';
        }
        break;
      }
      case 'w:ind': {
        const left = parseTwips(a['w:left'] ?? a['w:start']);
        const right = parseTwips(a['w:right'] ?? a['w:end']);
        if (left !== undefined) p.indLeft = left;
        if (right !== undefined) p.indRight = right;
        const first = parseTwips(a['w:firstLine']);
        const hanging = parseTwips(a['w:hanging']);
        if (hanging !== undefined) p.indFirst = -hanging;
        else if (first !== undefined) p.indFirst = first;
        const fc = parseIntAttr(a['w:firstLineChars']);
        const hc = parseIntAttr(a['w:hangingChars']);
        if (hc) p.indFirstChars = -hc;
        else if (fc) p.indFirstChars = fc;
        else if (fc === 0 || hc === 0) p.indFirstChars = undefined;
        break;
      }
      case 'w:contextualSpacing':
        p.contextualSpacing = parseOnOff(c);
        break;
      case 'w:jc': {
        const jc = normalizeJc(a['w:val']);
        if (jc) p.jc = jc;
        break;
      }
      case 'w:outlineLvl': {
        const lvl = parseIntAttr(a['w:val']);
        if (lvl !== undefined) p.outlineLvl = lvl;
        break;
      }
      case 'w:suppressAutoHyphens':
        p.suppressAutoHyphens = parseOnOff(c);
        break;
    }
  }
  return p;
}

export function parseRPr(rPr: XElement | undefined): RunProps {
  const r: RunProps = {};
  if (!rPr) return r;
  for (const c of rPr.children) {
    if (c.type !== 'element') continue;
    const a = c.attrs;
    switch (c.name) {
      case 'w:rFonts': {
        // Атрибут темы важнее явного имени шрифта на том же уровне.
        const slot = (theme?: string, name?: string): FontRef | undefined =>
          theme ? { theme } : name ? { name } : undefined;
        const ascii = slot(a['w:asciiTheme'], a['w:ascii']);
        const hAnsi = slot(a['w:hAnsiTheme'], a['w:hAnsi']);
        const eastAsia = slot(a['w:eastAsiaTheme'], a['w:eastAsia']);
        const cs = slot(a['w:cstheme'], a['w:cs']);
        if (ascii) r.fontAscii = ascii;
        if (hAnsi) r.fontHAnsi = hAnsi;
        if (eastAsia) r.fontEastAsia = eastAsia;
        if (cs) r.fontCs = cs;
        const hint = a['w:hint'];
        if (hint === 'eastAsia' || hint === 'cs' || hint === 'default') r.fontHint = hint;
        break;
      }
      case 'w:b':
        r.b = parseOnOff(c);
        break;
      case 'w:i':
        r.i = parseOnOff(c);
        break;
      case 'w:caps':
        r.caps = parseOnOff(c);
        break;
      case 'w:smallCaps':
        r.smallCaps = parseOnOff(c);
        break;
      case 'w:strike':
        r.strike = parseOnOff(c);
        break;
      case 'w:dstrike':
        r.dstrike = parseOnOff(c);
        break;
      case 'w:vanish':
        r.vanish = parseOnOff(c);
        break;
      case 'w:u':
        r.u = a['w:val'] ?? 'single';
        break;
      case 'w:color':
        r.color = (a['w:val'] ?? 'auto').toUpperCase() === 'AUTO' ? 'auto' : (a['w:val'] ?? '').toUpperCase();
        break;
      case 'w:highlight':
        r.highlight = a['w:val'] ?? 'none';
        break;
      case 'w:shd': {
        const fill = a['w:fill'];
        if (fill) r.shading = fill.toUpperCase() === 'AUTO' ? 'auto' : fill.toUpperCase();
        break;
      }
      case 'w:sz': {
        const sz = parseHalfPoints(a['w:val']);
        if (sz !== undefined) r.sz = sz;
        break;
      }
      case 'w:szCs': {
        const sz = parseHalfPoints(a['w:val']);
        if (sz !== undefined) r.szCs = sz;
        break;
      }
      case 'w:vertAlign': {
        const v = a['w:val'];
        if (v === 'superscript' || v === 'subscript' || v === 'baseline') r.vertAlign = v;
        break;
      }
      case 'w:lang':
        if (a['w:val']) r.lang = a['w:val'];
        break;
      case 'w:spacing': {
        const sp = parseTwips(a['w:val']);
        if (sp !== undefined) r.spacing = sp;
        break;
      }
      case 'w:w': {
        const w = parseIntAttr(a['w:val']);
        if (w !== undefined) r.w = w;
        break;
      }
    }
  }
  return r;
}

/** Межстрочный интервал в «строках», если он задан множителем. */
export function lineMultiple(p: ParaProps): number | undefined {
  if (p.line === undefined) return 1;
  if (p.lineRule && p.lineRule !== 'auto') return undefined;
  return p.line / 240;
}
